// Compass design system color tokens
// Maps to cx.css CSS custom properties (--cx-color-*)
// See: https://gamma.compass.com/ucfe-assets/cx/3/cx.css

export const white = '#ffffff'
export const black = '#000000'

const background = {
	base: white,
	shade: '#FAFAFA', // --cx-color-grey10
	scrim: 'rgba(0, 0, 0, 0.25)',
}

const brand = {
	primary: '#0064E5', // --cx-color-interactive
	primaryHighlight: '#E0EFFF',
	primaryDepth: '#004FB3',
	onPrimary: white,
	onInversePrimary: '#80B2F2',
	secondary: '#eb672f',
	secondaryHighlight: '#fce8e0',
	secondaryDepth: '#c85828',
	onSecondary: white,
	onInverseSecondary: '#f3a482',
	aiGradientPurple: '#A531AF',
	aiGradientBlue: '#0A19C1',
	aiPrimary: '#030330',
}

const strokeBrand = {
	primary: '#0064E5', // --cx-color-interactive
	primaryDepth: '#004FB3',
	secondary: '#eb672f',
	secondaryDepth: '#c85828',
}

const surfaceNeutrals = {
	base: '#ffffff', // --cx-color-white
	lowest: '#FAFAFA', // --cx-color-grey10
	low: '#DADADA', // --cx-color-grey40
	high: '#8D8D8D', // --cx-color-grey70
	highest: '#4E4E4E', // --cx-color-grey90
	inverse: '#171717', // --cx-color-grey120
	inverseLight: white,
	transparent: 'transparent',
	skeleton: '#E8E8E8', // --cx-color-grey30
	tableHover: '#F0F8FF',
}

const surfaceSpectrum = {
	pink: '#F9DEEC',
	fuschia: '#F6DBFA',
	purple: '#E7D9FC',
	blue: '#DFE9FD',
	teal: '#D9ECEB',
	green: '#DEEDE5',
	lime: '#F6F7E0',
	yellow: '#FEF5E0',
	orange: '#FCE7D9',
	red: '#F9E1E5',
}

const foregroundNeutrals = {
	onSurface: '#171717', // --cx-color-text
	onSurfaceVar01: '#6C6C6C', // --cx-color-textSubtle
	onSurfaceVar02: '#6C6C6C', // --cx-color-textSubtle
	onSurfaceVar03: '#8D8D8D', // --cx-color-grey70
	onSurfaceVar04: '#6C6C6C', // --cx-color-textSubtle
	onSurfaceVar05: '#C6C6C6', // --cx-color-grey50
	disabledOnSurface: '#ADADAD', // --cx-color-textDisabled
	disabledOnColorSurface: '#6C6C6C', // --cx-color-textSubtle
	onInverseSurface: '#FAFAFA', // --cx-color-grey10
	onColor: white,
	skeletonElement: '#E8E8E8', // --cx-color-grey30
	neutral70: '#8D8D8D', // --cx-color-grey70
}

const link = {
	default: '#0064E5', // --cx-color-link
	disabled: '#80B2F2',
	visited: '#7A3E7A',
	visitedDisabled: '#AF8BAF',
}

const foregroundSpectrum = {
	pink: '#6C1240',
	fuschia: '#63076F',
	purple: '#310077',
	blue: '#143778',
	teal: '#01413C',
	green: '#134428',
	lime: '#60651A',
	yellow: '#7B5E18',
	orange: '#773001',
	red: '#6C1D29',
}

const strokeNeutrals = {
	base: '#DADADA', // --cx-color-border
	low: '#E8E8E8', // --cx-color-grey30
	high: '#171717', // --cx-color-text
	disabledLight: '#ADADAD', // --cx-color-textDisabled
	disableDark: '#6C6C6C', // --cx-color-textSubtle
	focus: black, // --cx-color-black
}

const semiTransparentBlack = {
	brandHover: 'rgba(0, 0, 0, 0.1)',
	brandPress: 'rgba(0, 0, 0, 0.2)',
	neutralHover: 'rgba(0, 0, 0, 0.1)',
	neutralPress: 'rgba(0, 0, 0, 0.16)',
	disabled: 'rgba(0, 0, 0, 0.06)',
	linkActive: 'rgba(0, 0, 0, 0.3)',
	primaryHover: 'rgba(0, 100, 229, 0.1)',
	primaryPress: 'rgba(0, 100, 229, 0.2)',
	primaryDisabled: 'rgba(0, 100, 229, 0.12)',
	navHover: 'rgba(0, 0, 0, 0.09)',
	navActive: 'rgba(0, 0, 0, 0.18)',
}

const status = {
	error: '#AE0917', // --cx-color-critical
	success: '#3AAC6F', // --cx-color-success
	warning: '#FFBF00', // --cx-color-highlight
	information: '#0064E5', // --cx-color-interactive
	onError: white,
	onSuccess: white,
	onWarning: '#171717', // --cx-color-text
	onInformation: white,
}

const dataVisual = {
	category01: '#d67e51',
	category01Highlight: '#d67e514d',
	category02: '#7a2a0b',
	category02Highlight: '#7a2a0b4d',
	category03: '#ab922b',
	category03Highlight: '#ab922b4d',
	category04: '#2d3300',
	category04Highlight: '#2d33004d',
	category05: '#794b34',
	category05Highlight: '#794b344d',
	category06: '#7f7f7f',
	category06Highlight: '#7f7f7f4d',
	category07: '#530909',
	category07Highlight: '#5309094d',
	category1: '#7a2a0b',
	category1Highlight: '#7a2a0b4d',
	category2: '#d67e51',
	category2Highlight: '#d67e514d',
	category3: '#2d3300',
	category3Highlight: '#2d33004d',
	category4: '#ab922b',
	category4Highlight: '#ab922b4d',
	category5: '#530909',
	category5Highlight: '#5309094d',
	category6: '#7f7f7f',
	category6Highlight: '#7f7f7f4d',
	category7: '#794b34',
	category7Highlight: '#794b344d',
	gradientEnd: '#ffffff4d',
	green: '#3AAC6F', // --cx-color-success
	greenHighlight: '#deede5',
	negative: '#AE0917', // --cx-color-critical
	negativeHighlight: '#f9e1e5',
	positive: '#3AAC6F', // --cx-color-success
	positiveHighlight: '#deede5',
	red: '#AE0917', // --cx-color-critical
	redHighlight: '#f9e1e5',
}

const dataVisualSpectrum = {
	pink: '#D82380',
	fuschia: '#C60EDE',
	purple: '#6200EE',
	opened: '#299DE0',
	blue: '#276EF1',
	otherRevenue: '#130033',
	teal: '#038377',
	closed: '#0A466C',
	green: '#3AAC6F', // --cx-color-success
	lime: '#C0CA33',
	yellow: '#FFBF00', // --cx-color-highlight
	pending: '#389E7A',
	orange: '#EE6002',
	red: '#AE0917', // --cx-color-critical
	azure: '#0A466C',
	vivdBlue: '#299DE0',
	grey: '#7F7F7F',
	contactInitiated: '#094A53',
	meetingEngaging: '#1A1C25',
	negotiating: '#433479',
}

const legacyChart = {
	deepPurple: '#3a72a3',
	amber: '#ffa000',
	pink: '#d81b60',
	purple: '#9e70e5',
	lime: '#64c1ac',
	cyan: '#7aa1eb',
	gridLines: '#dcddde',
}

const legacyGraph = {
	dataGraph01: '#266DF0',
	dataGraph02: '#EE6002',
	dataGraph03: '#6200EE',
	dataGraph04: '#E8E8E8', // --cx-color-grey30
	dataGraph05: '#3a72a3',
	dataGraph06: '#7aa1eb',
	dataGraph07: '#64c1ac',
	dataGraph08: '#ff9800',
	dataGraph09: '#d81b60',
	dataGraph10: '#3a72a3',
}

export const compassPalette = {
	surface: {
		brand: {
			primary: brand.primary,
			primaryHighlight: brand.primaryHighlight,
			secondary: brand.secondary,
			secondaryHighlight: brand.secondaryHighlight,
			aiGradientPurple: brand.aiGradientPurple,
			aiGradientBlue: brand.aiGradientBlue,
			aiPrimary: brand.aiPrimary,
		},
		neutral: {
			t1Base: surfaceNeutrals.base,
			t2Lowest: surfaceNeutrals.lowest,
			t3Low: surfaceNeutrals.low,
			t4High: surfaceNeutrals.high,
			t5Highest: surfaceNeutrals.highest,
			inverse: surfaceNeutrals.inverse,
			inverseLight: surfaceNeutrals.inverseLight,
			transparent: surfaceNeutrals.transparent,
			skeleton: surfaceNeutrals.skeleton,
			tableHover: surfaceNeutrals.tableHover,
		},
		spectrum: {
			pink: surfaceSpectrum.pink,
			fuschia: surfaceSpectrum.fuschia,
			purple: surfaceSpectrum.purple,
			blue: surfaceSpectrum.blue,
			teal: surfaceSpectrum.teal,
			green: surfaceSpectrum.green,
			lime: surfaceSpectrum.lime,
			yellow: surfaceSpectrum.yellow,
			orange: surfaceSpectrum.orange,
			red: surfaceSpectrum.red,
		},
	},
	foreground: {
		brand: {
			primary: brand.primary,
			primaryDepth: brand.primaryDepth,
			onPrimary: brand.onPrimary,
			onInversePrimary: brand.onInversePrimary,
			secondary: brand.secondary,
			secondaryDepth: brand.secondaryDepth,
			onSecondary: brand.onSecondary,
			onInverseSecondary: brand.onInverseSecondary,
			aiGradientPurple: brand.aiGradientPurple,
			aiGradientBlue: brand.aiGradientBlue,
			aiPrimary: brand.aiPrimary,
		},
		neutral: {
			onSurface: foregroundNeutrals.onSurface,
			onSurfaceVar01: foregroundNeutrals.onSurfaceVar01,
			onSurfaceVar02: foregroundNeutrals.onSurfaceVar02,
			onSurfaceVar03: foregroundNeutrals.onSurfaceVar03,
			onSurfaceVar04: foregroundNeutrals.onSurfaceVar04,
			onSurfaceVar05: foregroundNeutrals.onSurfaceVar05,
			disabledOnSurface: foregroundNeutrals.disabledOnSurface,
			disabledOnColorSurface: foregroundNeutrals.disabledOnColorSurface,
			onInverseSurface: foregroundNeutrals.onInverseSurface,
			onColor: foregroundNeutrals.onColor,
			skeletonElement: foregroundNeutrals.skeletonElement,
			neutral70: foregroundNeutrals.neutral70,
		},
		link: {
			default: link.default,
			disabled: link.disabled,
			visitedDefault: link.visited,
			visitedDisabled: link.visitedDisabled,
		},
		spectrum: {
			pink: foregroundSpectrum.pink,
			fuschia: foregroundSpectrum.fuschia,
			purple: foregroundSpectrum.purple,
			blue: foregroundSpectrum.blue,
			teal: foregroundSpectrum.teal,
			green: foregroundSpectrum.green,
			lime: foregroundSpectrum.lime,
			yellow: foregroundSpectrum.yellow,
			orange: foregroundSpectrum.orange,
			red: foregroundSpectrum.red,
		},
	},
	stroke: {
		brand: {
			primary: strokeBrand.primary,
			primaryDepth: strokeBrand.primaryDepth,
			secondary: strokeBrand.secondary,
			secondaryDepth: strokeBrand.secondaryDepth,
		},
		neutral: {
			base: strokeNeutrals.base,
			low: strokeNeutrals.low,
			high: strokeNeutrals.high,
			disabledLight: strokeNeutrals.disabledLight,
			disabledDark: strokeNeutrals.disableDark,
			focus: strokeNeutrals.focus,
		},
	},
	interaction: {
		brandHoverOpacity: semiTransparentBlack.brandHover,
		brandPressOpacity: semiTransparentBlack.brandPress,
		neutralHoverOpacity: semiTransparentBlack.neutralHover,
		neutralPressOpacity: semiTransparentBlack.neutralPress,
		disabledOpacity: semiTransparentBlack.disabled,
		linkActiveOpacity: semiTransparentBlack.linkActive,
		primaryHoverOpacity: semiTransparentBlack.primaryHover,
		primaryPressOpacity: semiTransparentBlack.primaryPress,
		navHoverOpacity: semiTransparentBlack.navHover,
		navActiveOpacity: semiTransparentBlack.navActive,
	},

	dataVisual: {
		spectrum: {
			pink: dataVisualSpectrum.pink,
			fuschia: dataVisualSpectrum.fuschia,
			purple: dataVisualSpectrum.purple,
			blue: dataVisualSpectrum.blue,
			teal: dataVisualSpectrum.teal,
			green: dataVisualSpectrum.green,
			lime: dataVisualSpectrum.lime,
			yellow: dataVisualSpectrum.yellow,
			orange: dataVisualSpectrum.orange,
			red: dataVisualSpectrum.red,
			azure: dataVisualSpectrum.azure,
			vivdBlue: dataVisualSpectrum.vivdBlue,
			opened: dataVisualSpectrum.opened,
			otherRevenue: dataVisualSpectrum.otherRevenue,
			closed: dataVisualSpectrum.closed,
			pending: dataVisualSpectrum.pending,
			grey: dataVisualSpectrum.grey,
			contactInitiated: dataVisualSpectrum.contactInitiated,
			meetingEngaging: dataVisualSpectrum.meetingEngaging,
			negotiating: dataVisualSpectrum.negotiating,
		},
		category01: dataVisual.category01,
		category01Highlight: dataVisual.category01Highlight,
		category02: dataVisual.category02,
		category02Highlight: dataVisual.category02Highlight,
		category03: dataVisual.category03,
		category03Highlight: dataVisual.category03Highlight,
		category04: dataVisual.category04,
		category04Highlight: dataVisual.category04Highlight,
		category05: dataVisual.category05,
		category05Highlight: dataVisual.category05Highlight,
		category06: dataVisual.category06,
		category06Highlight: dataVisual.category06Highlight,
		category07: dataVisual.category07,
		category07Highlight: dataVisual.category07Highlight,
		category1: dataVisual.category1,
		category1Highlight: dataVisual.category1Highlight,
		category2: dataVisual.category2,
		category2Highlight: dataVisual.category2Highlight,
		category3: dataVisual.category3,
		category3Highlight: dataVisual.category3Highlight,
		category4: dataVisual.category4,
		category4Highlight: dataVisual.category4Highlight,
		category5: dataVisual.category5,
		category5Highlight: dataVisual.category5Highlight,
		category6: dataVisual.category6,
		category6Highlight: dataVisual.category6Highlight,
		category7: dataVisual.category7,
		category7Highlight: dataVisual.category7Highlight,
		gradientEnd: dataVisual.gradientEnd,
		green: dataVisual.green,
		greenHighlight: dataVisual.greenHighlight,
		negative: dataVisual.negative,
		negativeHighlight: dataVisual.negativeHighlight,
		positive: dataVisual.positive,
		positiveHighlight: dataVisual.positiveHighlight,
		red: dataVisual.red,
		redHighlight: dataVisual.redHighlight,
	},

	// MUI core palette
	background: {
		paper: background.base,
		default: background.shade,
		scrim: background.scrim,
	},
	primary: {
		main: brand.primary, // #0064E5 --cx-color-interactive
		dark: brand.primaryDepth,
		contrastText: brand.onPrimary,
	},
	secondary: {
		main: brand.secondary,
		light: brand.secondaryHighlight,
		contrastText: brand.onSecondary,
	},
	success: {
		main: status.success, // #3AAC6F --cx-color-success
		contrastText: status.onSuccess,
	},
	info: {
		main: status.information, // #0064E5 --cx-color-interactive
		contrastText: status.onInformation,
	},
	error: {
		main: status.error, // #AE0917 --cx-color-critical
		contrastText: status.onError,
	},
	warning: {
		main: status.warning, // #FFBF00 --cx-color-highlight
		contrastText: status.onWarning,
	},
	text: {
		primary: foregroundNeutrals.onSurface, // #171717 --cx-color-text
		secondary: foregroundNeutrals.onSurfaceVar01, // #6C6C6C --cx-color-textSubtle
	},

	/** @deprecated do not use, refactor all references */
	chart: {
		deepPurple: legacyChart.deepPurple,
		amber: legacyChart.amber,
		pink: legacyChart.pink,
		purple: legacyChart.purple,
		lime: legacyChart.lime,
		cyan: legacyChart.cyan,
		gridLines: legacyChart.gridLines,
	},
	/** @deprecated do not use, refactor all references */
	graph: {
		dataGraph01: legacyGraph.dataGraph01,
		dataGraph02: legacyGraph.dataGraph02,
		dataGraph03: legacyGraph.dataGraph03,
		dataGraph04: legacyGraph.dataGraph04,
		dataGraph05: legacyGraph.dataGraph05,
		dataGraph06: legacyGraph.dataGraph06,
		dataGraph07: legacyGraph.dataGraph07,
		dataGraph08: legacyGraph.dataGraph08,
		dataGraph09: legacyGraph.dataGraph09,
		dataGraph10: legacyGraph.dataGraph10,
	},
	/** @deprecated do not use, refactor all references */
	surfaceVariant: '#474747',
}
